import { InspectionQuestion } from "../types/inspectionWorkflow";
import { InspectionResult, isVerified } from "src/services/inspectionService";

/** A single grouped-results entry: a question id and all of its captured results. */
export type GroupedResultEntry = [string, InspectionResult[]];

/** True when a result is still awaiting AI analysis (aiResult/confirmedResult === "PENDING"). */
export const isAnalysisPending = (result: InspectionResult): boolean =>
    result.aiResult?.toUpperCase() === "PENDING" ||
    result.confirmedResult?.toUpperCase() === "PENDING";

/**
 * Orders grouped inspection results by the workflow's question order.
 *
 * Results are grouped by `questionId` upstream (see
 * `InspectionService.fetchInspectionWithPresignedUrls`). Both the review page
 * and the resume-draft page need to render those groups in the same order the
 * questions appear in the workflow, so this ordering lives in one shared,
 * pure helper.
 */
export const orderGroupedResultsByQuestion = (
    groupedResults: Record<string, InspectionResult[]>,
    questions: InspectionQuestion[]
): GroupedResultEntry[] =>
    Object.entries(groupedResults).sort(
        ([a], [b]) =>
            questions.findIndex((q) => q.id === a) -
            questions.findIndex((q) => q.id === b)
    );

/** Rollup of checkpoint verification state across a whole inspection. */
export interface VerificationSummary {
    /** Number of checkpoints that require explicit verification. */
    requiredCount: number;
    /** Of those, how many have been verified. */
    verifiedCount: number;
    /** True when there is at least one checkpoint requiring verification and all are verified. */
    allVerified: boolean;
}

/**
 * Whether a question requires verification. Mirrors CheckpointList: object-presence
 * always requires it; container requires it when AI-assisted or auto-calculated.
 */
const questionRequiresVerification = (
    results: InspectionResult[],
    question: InspectionQuestion | undefined
): boolean => {
    const firstResult = results[0];
    if (!firstResult) return false;

    const hasObjectPresenceChecks =
        firstResult.objectPresenceChecks !== null &&
        firstResult.objectPresenceChecks !== undefined &&
        firstResult.objectPresenceChecks.length > 0;
    if (hasObjectPresenceChecks) return true;

    const isAI =
        firstResult.aiReasoning !== null && firstResult.aiReasoning !== "";
    const isAutoCalculated = question?.mode === "auto-calculated";
    return isAI || isAutoCalculated;
};

/** Rolls up verification across the inspection (stamp lives on the first result of each question). */
export const getVerificationSummary = (
    groupedResults: Record<string, InspectionResult[]>,
    questions: InspectionQuestion[]
): VerificationSummary => {
    let requiredCount = 0;
    let verifiedCount = 0;

    for (const [questionId, results] of Object.entries(groupedResults)) {
        const question = questions.find((q) => q.id === questionId);
        if (!questionRequiresVerification(results, question)) continue;

        requiredCount += 1;
        if (results.length > 0 && isVerified(results[0])) {
            verifiedCount += 1;
        }
    }

    return {
        requiredCount,
        verifiedCount,
        allVerified: requiredCount > 0 && verifiedCount === requiredCount,
    };
};
